import type { ReactNode } from "react";

type UnitMap = Record<string, number> | null | undefined;

const splitNames = (value?: string | null) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const unitsFor = (map: UnitMap, name: string): number | null => {
  if (!map) return null;
  if (map[name] != null && Number.isFinite(Number(map[name]))) {
    return Number(map[name]);
  }
  const key = Object.keys(map).find(
    (candidate) => candidate.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (key == null || map[key] == null || !Number.isFinite(Number(map[key]))) {
    return null;
  }
  return Number(map[key]);
};

type Credit = {
  name: string;
  design: number | null;
  model: number | null;
};

/** Design counts stay plain. Model counts are a second pair of parentheses. */
export const workCredits = (
  designedBy?: string | null,
  designUnit?: number | null,
  designerUnits?: UnitMap,
  modelBy?: string | null,
  modelUnits?: UnitMap,
): Credit[] => {
  const people: { name: string; design: boolean }[] = [];
  const index = new Map<string, number>();
  const touch = (name: string, design: boolean) => {
    const key = name.trim().toLowerCase();
    if (!key) return;
    const at = index.get(key);
    if (at == null) {
      index.set(key, people.length);
      people.push({ name: name.trim(), design });
      return;
    }
    if (design) people[at].design = true;
  };
  splitNames(designedBy).forEach((name) => touch(name, true));
  splitNames(modelBy).forEach((name) => touch(name, false));

  const designNames = people.filter((person) => person.design);
  const hasPerDesign = designNames.some(
    (person) => unitsFor(designerUnits, person.name) != null,
  );

  return people.map((person, i) => {
    const rawDesign = unitsFor(designerUnits, person.name);
    const rawModel = unitsFor(modelUnits, person.name);
    const model = rawModel != null && rawModel > 0 ? rawModel : null;

    let design: number | null = null;
    if (hasPerDesign) {
      if (rawDesign != null && rawDesign > 0) design = rawDesign;
      else if (rawDesign === 0 && model == null && person.design) design = 0;
    } else if (person.design && designUnit != null) {
      const laterDesign = people.slice(i + 1).some((next) => next.design);
      if (designNames.length === 1 || !laterDesign) design = designUnit;
    }

    return { name: person.name, design, model };
  });
};

export const personWorkCreditTotal = (
  personName?: string | null,
  designedBy?: string | null,
  designUnit?: number | null,
  designerUnits?: UnitMap,
  modelBy?: string | null,
  modelUnits?: UnitMap,
) => {
  const key = String(personName || "").trim().toLowerCase();
  if (!key) return 0;
  return workCredits(
    designedBy,
    designUnit,
    designerUnits,
    modelBy,
    modelUnits,
  ).reduce((sum, credit) => {
    if (credit.name.trim().toLowerCase() !== key) return sum;
    return sum + (credit.design ?? 0) + (credit.model ?? 0);
  }, 0);
};

export const workCreditTotal = (
  designedBy?: string | null,
  designUnit?: number | null,
  designerUnits?: UnitMap,
  modelBy?: string | null,
  modelUnits?: UnitMap,
) =>
  workCredits(designedBy, designUnit, designerUnits, modelBy, modelUnits).reduce(
    (sum, credit) => sum + (credit.design ?? 0) + (credit.model ?? 0),
    0,
  );

export const workCreditText = (
  designedBy?: string | null,
  designUnit?: number | null,
  designerUnits?: UnitMap,
  modelBy?: string | null,
  modelUnits?: UnitMap,
) =>
  workCredits(designedBy, designUnit, designerUnits, modelBy, modelUnits)
    .map((credit) => {
      const design = credit.design != null ? `(${credit.design})` : "";
      const model = credit.model != null ? `(${credit.model})` : "";
      const counts = `${design}${model}`;
      return counts ? `${credit.name} ${counts}` : credit.name;
    })
    .join(", ");

const modelUnitTotal = (modelUnits?: UnitMap) => {
  if (!modelUnits) return 0;
  return Object.values(modelUnits).reduce((sum, value) => {
    const units = Number(value);
    return sum + (Number.isFinite(units) && units > 0 ? units : 0);
  }, 0);
};

/** QC-2 shows the case design units, then model units in green. */
export const qc2CreditText = (
  name?: string | null,
  designUnit?: number | null,
  modelUnits?: UnitMap,
) => {
  const label = String(name || "").trim();
  if (!label) return "";
  const model = modelUnitTotal(modelUnits);
  const design = designUnit != null ? `(${designUnit})` : "";
  const modelText = model > 0 ? `(${model})` : "";
  return `${label}${design}${modelText}`;
};

export const Qc2CreditLabel = ({
  name,
  designUnit,
  modelUnits,
}: {
  name?: string | null;
  designUnit?: number | null;
  modelUnits?: UnitMap;
}): ReactNode => {
  const label = String(name || "").trim();
  if (!label) return null;
  const model = modelUnitTotal(modelUnits);
  return (
    <>
      {label}
      {designUnit != null ? ` (${designUnit})` : null}
      {model > 0 ? (
        <span className="font-semibold text-emerald-600">({model})</span>
      ) : null}
    </>
  );
};

export const WorkCreditLabel = ({
  designedBy,
  designUnit,
  designerUnits,
  modelBy,
  modelUnits,
}: {
  designedBy?: string | null;
  designUnit?: number | null;
  designerUnits?: UnitMap;
  modelBy?: string | null;
  modelUnits?: UnitMap;
}): ReactNode => {
  const credits = workCredits(
    designedBy,
    designUnit,
    designerUnits,
    modelBy,
    modelUnits,
  );
  if (!credits.length) return null;

  return (
    <>
      {credits.map((credit, i) => (
        <span key={`${credit.name}-${i}`}>
          {i > 0 ? ", " : null}
          {credit.name}
          {credit.design != null ? ` (${credit.design})` : null}
          {credit.model != null ? (
            <span className="font-semibold text-emerald-600">
              {credit.design != null ? `(${credit.model})` : ` (${credit.model})`}
            </span>
          ) : null}
        </span>
      ))}
    </>
  );
};
